import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { FileText, Truck, Route, TrendingUp, Package, AlertCircle, Loader2, ShoppingCart, Briefcase, ArrowRight } from 'lucide-react';
import api from '../services/api';
import { formatarMoeda, formatarNumero } from '../utils/formatters';
import { useAuth } from '../contexts/AuthContext';
import { useEmpresa } from '../contexts/EmpresaContext';

function CardKPI({ titulo, valor, icone: Icone, cor, subtitulo }) {
  return (
    <div className="card flex items-start gap-4">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${cor}`}>
        <Icone size={20} className="text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-slate-500 font-medium">{titulo}</p>
        <p className="text-2xl font-bold text-white mt-0.5">{valor}</p>
        {subtitulo && <p className="text-xs text-slate-500 mt-0.5">{subtitulo}</p>}
      </div>
    </div>
  );
}

const TooltipCustom = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface-card border border-surface-border rounded-lg px-3 py-2 text-sm shadow-xl">
      <p className="text-slate-400 mb-1">{label}</p>
      <p className="text-primary-400 font-semibold">{formatarMoeda(payload[0]?.value || 0)}</p>
      <p className="text-slate-300">{formatarNumero(payload[1]?.value || 0)} docs</p>
    </div>
  );
};

export default function DashboardPage() {
  const { usuario }  = useAuth();
  const { empresaAtual, carregando: carregandoEmpresa } = useEmpresa() || {};
  const navigate     = useNavigate();
  const isCliente    = usuario?.perfil === 'cliente';

  const [dados,    setDados]    = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [erro,     setErro]     = useState(null);

  useEffect(() => {
    if (carregandoEmpresa) return; // espera resolver a empresa selecionada antes de buscar
    setLoading(true);
    api.get('/relatorios/dashboard', { params: empresaAtual ? { empresa_id: empresaAtual.id } : {} })
      .then(r => setDados(r.data))
      .catch(() => setErro('Erro ao carregar dashboard.'))
      .finally(() => setLoading(false));
  }, [empresaAtual, carregandoEmpresa]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 size={32} className="animate-spin text-primary-500" />
    </div>
  );

  if (erro) return (
    <div className="flex items-center gap-2 text-red-400 bg-red-900/20 border border-red-800/30 rounded-xl p-4">
      <AlertCircle size={18} /> {erro}
    </div>
  );

  const { totais = {}, evolucao = [] } = dados || {};

  // ── Portal do cliente: acesso rápido ──────────────────────
  if (isCliente) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Card Compras */}
          <button onClick={() => navigate('/documentos?operacao=recebimento')}
            className="card text-left hover:border-sky-600/40 transition-all group cursor-pointer">
            <div className="flex items-start justify-between">
              <div className="w-12 h-12 rounded-xl bg-sky-900/40 border border-sky-800/30 flex items-center justify-center mb-4">
                <ShoppingCart size={22} className="text-sky-400" />
              </div>
              <ArrowRight size={16} className="text-slate-600 group-hover:text-sky-400 transition-colors mt-1" />
            </div>
            <p className="text-xs text-slate-500 font-medium mb-1">Notas de Compra</p>
            <p className="text-3xl font-bold text-white">{formatarNumero(totais.total_nfe || 0)}</p>
            <p className="text-xs text-sky-400 mt-2">NF-e de recebimento · clique para ver</p>
          </button>

          {/* Card Vendas */}
          <button onClick={() => navigate('/documentos?operacao=emissao')}
            className="card text-left hover:border-emerald-600/40 transition-all group cursor-pointer">
            <div className="flex items-start justify-between">
              <div className="w-12 h-12 rounded-xl bg-emerald-900/40 border border-emerald-800/30 flex items-center justify-center mb-4">
                <TrendingUp size={22} className="text-emerald-400" />
              </div>
              <ArrowRight size={16} className="text-slate-600 group-hover:text-emerald-400 transition-colors mt-1" />
            </div>
            <p className="text-xs text-slate-500 font-medium mb-1">Notas de Venda / Serviço</p>
            <p className="text-3xl font-bold text-white">{formatarNumero((totais.total_nfce || 0) + (totais.total_nfse || 0))}</p>
            <p className="text-xs text-emerald-400 mt-2">NF-Ce e NF-Se emitidas · clique para ver</p>
          </button>
        </div>

        {/* Totais secundários */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <CardKPI titulo="CT-e"      valor={formatarNumero(totais.total_cte  || 0)} icone={Truck}       cor="bg-amber-600" />
          <CardKPI titulo="MDF-e"     valor={formatarNumero(totais.total_mdfe || 0)} icone={Route}       cor="bg-violet-600" />
          <CardKPI titulo="Este mês"  valor={formatarNumero(totais.documentos_mes_atual || 0)} icone={Package} cor="bg-primary-600" />
          <CardKPI titulo="Cancelados" valor={formatarNumero(totais.cancelados || 0)} icone={AlertCircle} cor="bg-red-700" />
        </div>

        {/* Gráfico */}
        <div className="card">
          <h2 className="text-sm font-semibold text-slate-200 mb-1">Evolução Mensal</h2>
          <p className="text-xs text-slate-500 mb-5">Últimos 12 meses</p>
          {evolucao.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-slate-600 text-sm">
              Nenhum dado disponível ainda.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={evolucao} barCategoryGap="30%">
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                <XAxis dataKey="mes" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis hide />
                <Tooltip content={<TooltipCustom />} cursor={{ fill: '#1e293b' }} />
                <Bar dataKey="quantidade" fill="#6366f1" radius={[4,4,0,0]} name="Qtd" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    );
  }

  // ── Dashboard admin/contador ───────────────────────────────
  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <CardKPI
          titulo="NF-e Armazenadas"
          valor={formatarNumero(totais.total_nfe || 0)}
          icone={FileText}
          cor="bg-primary-600"
          subtitulo={`${formatarNumero(totais.documentos_mes_atual || 0)} este mês`}
        />
        <CardKPI
          titulo="NF-Ce Armazenadas"
          valor={formatarNumero(totais.total_nfce || 0)}
          icone={ShoppingCart}
          cor="bg-sky-600"
        />
        <CardKPI
          titulo="NF-Se Armazenadas"
          valor={formatarNumero(totais.total_nfse || 0)}
          icone={Briefcase}
          cor="bg-teal-600"
        />
        <CardKPI
          titulo="CT-e Armazenados"
          valor={formatarNumero(totais.total_cte || 0)}
          icone={Truck}
          cor="bg-emerald-600"
        />
        <CardKPI
          titulo="MDF-e Armazenados"
          valor={formatarNumero(totais.total_mdfe || 0)}
          icone={Route}
          cor="bg-amber-600"
        />
        <CardKPI
          titulo="Valor Total Emitido"
          valor={formatarMoeda(totais.valor_total || 0)}
          icone={TrendingUp}
          cor="bg-violet-600"
          subtitulo="Todos os documentos"
        />
      </div>

      {/* Linha 2: Novos + Cancelados */}
      <div className="grid grid-cols-2 gap-4">
        <div className="card">
          <div className="flex items-center gap-2 mb-1">
            <Package size={16} className="text-primary-400" />
            <p className="text-xs text-slate-500 font-medium">Recebidos nas últimas 24h</p>
          </div>
          <p className="text-3xl font-bold text-white">{formatarNumero(totais.novos_24h || 0)}</p>
          <p className="text-xs text-emerald-400 mt-1">Sincronizados pelo ERP</p>
        </div>
        <div className="card">
          <div className="flex items-center gap-2 mb-1">
            <AlertCircle size={16} className="text-red-400" />
            <p className="text-xs text-slate-500 font-medium">Documentos Cancelados</p>
          </div>
          <p className="text-3xl font-bold text-white">{formatarNumero(totais.cancelados || 0)}</p>
          <p className="text-xs text-slate-500 mt-1">Total histórico</p>
        </div>
      </div>

      {/* Gráfico de evolução mensal */}
      <div className="card">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-sm font-semibold text-slate-200">Evolução Mensal</h2>
            <p className="text-xs text-slate-500">Últimos 12 meses — valor emitido e quantidade</p>
          </div>
        </div>

        {evolucao.length === 0 ? (
          <div className="flex items-center justify-center h-40 text-slate-600 text-sm">
            Nenhum dado disponível ainda.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={evolucao} barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
              <XAxis dataKey="mes" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip content={<TooltipCustom />} cursor={{ fill: '#1e293b' }} />
              <Bar dataKey="valor"      fill="#6366f1" radius={[4,4,0,0]} name="Valor" />
              <Bar dataKey="quantidade" fill="#334155" radius={[4,4,0,0]} name="Qtd" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
